import React from 'react'

import {some} from 'es-toolkit/compat'
import {getIn, useFormik} from 'formik'
import {useNavigate} from 'react-router'
import * as Yup from 'yup'

import * as providerInfoApi from '../../services/api/provider-info'
import {goToEditProviderInfo} from '../../services/navigation'
import {
  PossiblyMixedValue,
  isMixedValue,
  toMixedValidationSchema,
  toNumberValue,
  toPossiblyMixedValue,
  toStringValue,
  toTouchedFormData,
  unlockMixedValuesLabel,
} from '../utils/form'

import {Dimension, EditProviderInfoTab, ProviderInfo} from '../../types'

import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import NumberInputField from '../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../commons/src/components/paragraph'
import Stack from '../../../../commons/src/components/stack'
import MixedValueFormSection from '../../components/mixed-value-form-section'

import SaveButtonPartial from '../save-button-partial'

type Props = {
  providerInfos: Array<ProviderInfo>
}

export type SingleFormData = {
  minimumDimensionsX: string
  minimumDimensionsY: string
  minimumDimensionsZ: string
  maximumDimensionsX: string
  maximumDimensionsY: string
  maximumDimensionsZ: string
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

type MixedFormData = {
  minimumDimensionsX: PossiblyMixedValue<string>
  minimumDimensionsY: PossiblyMixedValue<string>
  minimumDimensionsZ: PossiblyMixedValue<string>
  maximumDimensionsX: PossiblyMixedValue<string>
  maximumDimensionsY: PossiblyMixedValue<string>
  maximumDimensionsZ: PossiblyMixedValue<string>
  formikGeneral?: unknown // Need type to be able to set general validation error.
}

export const initialValues = {
  minimumDimensionsX: '',
  minimumDimensionsY: '',
  minimumDimensionsZ: '',
  maximumDimensionsX: '',
  maximumDimensionsY: '',
  maximumDimensionsZ: '',
}

const isAnyOtherDimensionSet = (dimensionKey: keyof typeof initialValues, ctx: Yup.TestContext) => {
  const keys = Object.keys(initialValues).filter(key => key !== dimensionKey)

  return some(keys, key => ctx.parent[key] !== undefined)
}

export const validationSchema = {
  minimumDimensionsX: Yup.number()
    .moreThan(0)
    .label('X')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.maximumDimensionsX &&
          value > parseFloat(ctx.parent.maximumDimensionsX)
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('minimumDimensionsX', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  minimumDimensionsY: Yup.number()
    .moreThan(0)
    .label('Y')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.maximumDimensionsY &&
          value > parseFloat(ctx.parent.maximumDimensionsY)
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('minimumDimensionsY', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  minimumDimensionsZ: Yup.number()
    .moreThan(0)
    .label('Z')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.maximumDimensionsZ &&
          value > parseFloat(ctx.parent.maximumDimensionsZ)
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('minimumDimensionsZ', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  maximumDimensionsX: Yup.number()
    .moreThan(0)
    .label('X')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.minimumDimensionsX &&
          parseFloat(ctx.parent.minimumDimensionsX) > value
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('maximumDimensionsX', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  maximumDimensionsY: Yup.number()
    .moreThan(0)
    .label('Y')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.minimumDimensionsY &&
          parseFloat(ctx.parent.minimumDimensionsY) > value
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('maximumDimensionsY', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
  maximumDimensionsZ: Yup.number()
    .moreThan(0)
    .label('Z')
    .test(
      'interval',
      'Maximum dimension has to be equal or larger than minimum dimension',
      (value, ctx) => {
        if (
          value &&
          ctx.parent.minimumDimensionsZ &&
          parseFloat(ctx.parent.minimumDimensionsZ) > value
        ) {
          return ctx.createError({path: ctx.path})
        }
        return true
      }
    )
    .test('required', 'Either set all dimensions or leave all empty', (value, ctx) => {
      if (value === undefined && isAnyOtherDimensionSet('maximumDimensionsZ', ctx)) {
        return ctx.createError({path: ctx.path})
      }
      return true
    }),
}

const providerInfosToFormData = (providerInfos: ProviderInfo[]): MixedFormData => ({
  minimumDimensionsX: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.minimumDimensions ? providerInfo.minimumDimensions[0] : null
    ),
    toStringValue
  ),
  minimumDimensionsY: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.minimumDimensions ? providerInfo.minimumDimensions[1] : null
    ),
    toStringValue
  ),
  minimumDimensionsZ: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.minimumDimensions ? providerInfo.minimumDimensions[2] : null
    ),
    toStringValue
  ),
  maximumDimensionsX: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.maximumDimensions ? providerInfo.maximumDimensions[0] : null
    ),
    toStringValue
  ),
  maximumDimensionsY: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.maximumDimensions ? providerInfo.maximumDimensions[1] : null
    ),
    toStringValue
  ),
  maximumDimensionsZ: toPossiblyMixedValue(
    providerInfos.map(providerInfo =>
      providerInfo.maximumDimensions ? providerInfo.maximumDimensions[2] : null
    ),
    toStringValue
  ),
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formDataTransforms: Record<string, (value: any) => any> = {
  minimumDimensionsX: toNumberValue,
  minimumDimensionsY: toNumberValue,
  minimumDimensionsZ: toNumberValue,
  maximumDimensionsX: toNumberValue,
  maximumDimensionsY: toNumberValue,
  maximumDimensionsZ: toNumberValue,
}

export const formDataToPartialRequest = (
  formData: Partial<SingleFormData>
): Partial<providerInfoApi.ProviderInfoRequest> => {
  const minimumDimensions: Dimension = [
    formDataTransforms.minimumDimensionsX(formData.minimumDimensionsX),
    formDataTransforms.minimumDimensionsY(formData.minimumDimensionsY),
    formDataTransforms.minimumDimensionsZ(formData.minimumDimensionsZ),
  ]

  const maximumDimensions: Dimension = [
    formDataTransforms.maximumDimensionsX(formData.maximumDimensionsX),
    formDataTransforms.maximumDimensionsY(formData.maximumDimensionsY),
    formDataTransforms.maximumDimensionsZ(formData.maximumDimensionsZ),
  ]

  return {
    minimumDimensions:
      minimumDimensions.filter(value => value !== null).length > 0 ? minimumDimensions : null,
    maximumDimensions:
      maximumDimensions.filter(value => value !== null).length > 0 ? maximumDimensions : null,
  }
}

const EditDimensionsPartial = ({providerInfos}: Props) => {
  const navigate = useNavigate()

  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const formik = useFormik<MixedFormData>({
    enableReinitialize: true,
    initialValues: {
      ...initialValues,
      ...providerInfosToFormData(providerInfos),
    },
    validationSchema: toMixedValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(touchedValues)

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        const nextProviderInfos = await providerInfoApi.patchMultiple(
          providerInfoRequest,
          providerInfoIds
        )

        goToEditProviderInfo(navigate, {
          providerInfos: nextProviderInfos,
          activeTab: EditProviderInfoTab.DIMENSIONS,
          replace: true,
        })

        setIsSubmitted(true)
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  const hasError = (name: string) => formik.submitCount > 0 && !!getIn(formik.errors, name)

  return (
    <form onSubmit={formik.handleSubmit}>
      {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
      <Stack space="300">
        <MixedValueFormSection
          locked={
            isMixedValue(formik.values.minimumDimensionsX) ||
            isMixedValue(formik.values.minimumDimensionsY) ||
            isMixedValue(formik.values.minimumDimensionsZ) ||
            isMixedValue(formik.values.maximumDimensionsX) ||
            isMixedValue(formik.values.maximumDimensionsY) ||
            isMixedValue(formik.values.maximumDimensionsZ)
          }
          onClick={() => {
            formik.setValues(initialValues)
          }}
          unlockButtonLabel={unlockMixedValuesLabel}
        >
          <Headline size="s" label="Min dimensions" className="u-margin-bottom" />
          <FormRow layout="thirds">
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.minimumDimensionsX}
            >
              <NumberInputField
                allowDecimals
                name="minimumDimensionsX"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.minimumDimensionsX)}
                value={
                  isMixedValue(formik.values.minimumDimensionsX)
                    ? initialValues.minimumDimensionsX
                    : formik.values.minimumDimensionsX
                }
                prefix="X"
                suffix="mm"
                appearance={hasError('minimumDimensionsX') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.minimumDimensionsY}
            >
              <NumberInputField
                allowDecimals
                name="minimumDimensionsY"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.minimumDimensionsY)}
                value={
                  isMixedValue(formik.values.minimumDimensionsY)
                    ? initialValues.minimumDimensionsY
                    : formik.values.minimumDimensionsY
                }
                prefix="Y"
                suffix="mm"
                appearance={hasError('minimumDimensionsY') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.minimumDimensionsZ}
            >
              <NumberInputField
                allowDecimals
                name="minimumDimensionsZ"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.minimumDimensionsZ)}
                value={
                  isMixedValue(formik.values.minimumDimensionsZ)
                    ? initialValues.minimumDimensionsZ
                    : formik.values.minimumDimensionsZ
                }
                prefix="Z"
                suffix="mm"
                appearance={hasError('minimumDimensionsZ') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
          </FormRow>
          <Headline size="s" label="Max dimensions" className="u-margin-bottom" />
          <FormRow layout="thirds">
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.maximumDimensionsX}
            >
              <NumberInputField
                allowDecimals
                name="maximumDimensionsX"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.maximumDimensionsX)}
                value={
                  isMixedValue(formik.values.maximumDimensionsX)
                    ? initialValues.maximumDimensionsX
                    : formik.values.maximumDimensionsX
                }
                prefix="X"
                suffix="mm"
                appearance={hasError('maximumDimensionsX') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.maximumDimensionsY}
            >
              <NumberInputField
                allowDecimals
                name="maximumDimensionsY"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.maximumDimensionsY)}
                value={
                  isMixedValue(formik.values.maximumDimensionsY)
                    ? initialValues.maximumDimensionsY
                    : formik.values.maximumDimensionsY
                }
                prefix="Y"
                suffix="mm"
                appearance={hasError('maximumDimensionsY') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
            <FieldNote
              appearance="error"
              note={formik.submitCount > 0 && formik.errors.maximumDimensionsZ}
            >
              <NumberInputField
                allowDecimals
                name="maximumDimensionsZ"
                mixedLabel="Mixed"
                mixed={isMixedValue(formik.values.maximumDimensionsZ)}
                value={
                  isMixedValue(formik.values.maximumDimensionsZ)
                    ? initialValues.maximumDimensionsZ
                    : formik.values.maximumDimensionsZ
                }
                prefix="Z"
                suffix="mm"
                appearance={hasError('maximumDimensionsZ') ? 'error' : undefined}
                onChange={(value, name) => formik.setFieldValue(name as string, value)}
                onBlur={formik.handleBlur}
              />
            </FieldNote>
          </FormRow>
        </MixedValueFormSection>
        <SaveButtonPartial formik={formik} isSubmitted={isSubmitted} />
      </Stack>
    </form>
  )
}

export default EditDimensionsPartial
